import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { I } from '../icons.jsx';
import { TextField, Footer, AuthDecor, VividWordmark } from '../primitives.jsx';
import { api } from '../api.js';
import { LangSwitch, useLang } from '../i18n.jsx';

// D3: the page the reset email links to (/reset-password/<token>, or the
// older /reset-password?token=… form of links already sent). Same
// password rule as sign-up (at least 8 characters, which the backend also
// enforces), plus a confirmation. The backend answers 400 INVALID_TOKEN for
// a link that is expired, already used or wrong. English or Arabic
// (``?lang=``, which the emailed link carries; see i18n.jsx).
const MIN_LENGTH = 8;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const { lang, dir, t, setLang } = useLang();
  const { token: pathToken } = useParams();
  const token = useMemo(
    () => (pathToken || params.get('token') || '').trim(),
    [pathToken, params],
  );
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  // 'form' | 'done' | 'invalid'
  const [phase, setPhase] = useState(token ? 'form' : 'invalid');

  const validate = () => {
    const e = {};
    if (!password) e.password = t('reset.err.choose');
    else if (password.length < MIN_LENGTH) e.password = t('common.minLength', { n: MIN_LENGTH });
    if (password && confirm !== password) e.confirm = t('common.mismatch');
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setLoading(true);
    try {
      await api.resetPassword(token, password);
      setPhase('done');
    } catch (err) {
      if (err.code === 'INVALID_TOKEN' || err.status === 400 || err.status === 404) {
        setPhase('invalid');
      } else if (err.status === 422) {
        setErrors({ password: t('common.minLength', { n: MIN_LENGTH }) });
      } else if (err.status === 429) {
        setErrors({ password: t('common.tooMany') });
      } else {
        setErrors({ password: t('reset.err.failed') });
      }
    } finally {
      setLoading(false);
    }
  };

  const shell = (children) => (
    <div dir={dir} lang={lang} className="min-h-screen flex flex-col bg-white">
      <div className="flex-1 grid lg:grid-cols-2">
        <div className="flex flex-col px-6 lg:px-12">
          <div className="h-20 flex items-center justify-between">
            <VividWordmark />
            <LangSwitch lang={lang} setLang={setLang} />
          </div>
          <div className="flex-1 flex items-start pt-[8vh] justify-center">
            <div className="w-full max-w-[420px]">{children}</div>
          </div>
          <Footer t={t} />
        </div>
        <div className="hidden lg:block"><AuthDecor t={t} /></div>
      </div>
    </div>
  );

  if (phase === 'done') {
    return shell(
      <>
        <div className="inline-flex w-12 h-12 rounded-full bg-stone-100 items-center justify-center text-ink">
          <I.CircleCheck size={22} />
        </div>
        <h1 className="mt-5 text-[28px] font-bold tracking-tight text-ink">{t('reset.done.title')}</h1>
        <p role="status" className="mt-2 text-[15px] text-stone-500">
          {t('reset.done.body')}
        </p>
        <Link to="/login" className="btn-primary w-full mt-8 inline-flex justify-center">{t('common.signIn')}</Link>
      </>,
    );
  }

  if (phase === 'invalid') {
    return shell(
      <>
        <div className="inline-flex w-12 h-12 rounded-full bg-stone-100 items-center justify-center text-ink">
          <I.AlertCircle size={22} />
        </div>
        <h1 className="mt-5 text-[28px] font-bold tracking-tight text-ink">{t('reset.invalid.title')}</h1>
        <p role="alert" className="mt-2 text-[15px] text-stone-500">
          {t('reset.invalid.body')}
        </p>
        <Link to="/forgot-password" className="btn-primary w-full mt-8 inline-flex justify-center">
          {t('reset.invalid.newLink')}
        </Link>
        <Link to="/login" className="mt-3 block w-full text-center text-[13px] text-stone-500 hover:text-ink">
          {t('common.backToSignIn')}
        </Link>
      </>,
    );
  }

  return shell(
    <form onSubmit={submit} noValidate>
      <div className="inline-flex w-12 h-12 rounded-full bg-stone-100 items-center justify-center text-ink">
        <I.Lock size={22} />
      </div>
      <h1 className="mt-5 text-[28px] font-bold tracking-tight text-ink">{t('reset.title')}</h1>
      <p className="mt-2 text-[15px] text-stone-500">{t('common.atLeast', { n: MIN_LENGTH })}</p>
      <div className="mt-7 space-y-4">
        <TextField label={t('reset.newPassword')} type={showPwd ? 'text' : 'password'} value={password}
          onChange={setPassword} error={errors.password || undefined} autoComplete="new-password"
          rightAdornment={
            <button type="button" className="p-1" aria-label={showPwd ? t('common.hidePassword') : t('common.showPassword')}
              onClick={() => setShowPwd((s) => !s)}>
              {showPwd ? <I.EyeOff size={16} /> : <I.Eye size={16} />}
            </button>
          } />
        <TextField label={t('reset.confirm')} type={showPwd ? 'text' : 'password'} value={confirm}
          onChange={setConfirm} error={errors.confirm || undefined} autoComplete="new-password" />
      </div>
      <div className="pt-7">
        <button type="submit" disabled={!password || !confirm || loading} className="btn-primary w-full">
          {loading ? t('reset.saving') : t('reset.save')}
        </button>
      </div>
    </form>,
  );
}
